import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { MONTHS } from './consts';
import { getMonth, getYear } from 'date-fns';
import { Icon } from '@src/atoms/Icon';
import { Box } from '@src/atoms/Box';
import { IComponentChildrenProps } from '@src/models/props';
import { Popover } from '@src/atoms/Popover';
import { useOpen } from '@src/hooks/useOpen';
import { getYearList } from './utils';
import { useClickOutside } from '@src/hooks/useClickOutside';
import { SwipeDialogContext } from '@src/molecules/SwipeDialog';
import { Text } from '@src/atoms/Text';
import styles from './DatePickerCustomHeader.scss';
import { TDatePickerTheme } from '@src/atoms/DatePicker/models';

type TBtnKind = 'year' | 'month';

interface DatePickerCustomHeaderBtnProps extends IComponentChildrenProps {
  id: string;
  list: Array<string | number>;
  kind: TBtnKind;
  onClick: (kind: TBtnKind, v: number) => void;
  theme: TDatePickerTheme;
}

const DatePickerCustomHeaderBtn: FC<DatePickerCustomHeaderBtnProps> = ({
  id,
  kind,
  children,
  list,
  onClick,
  theme,
}) => {
  const [ref, setRef] = useState<HTMLElement | null>(null);
  const { open, onToggle, onClose } = useOpen(false);
  const { swipeController } = useContext(SwipeDialogContext);

  const isDarkTheme = theme === 'dark';

  useClickOutside(ref, onClose);

  const handleClick = useCallback(
    (v) => () => {
      if (kind === 'month') {
        onClick(kind, MONTHS.indexOf(v));
      } else {
        onClick(kind, v);
      }
      onClose();
    },
    [kind, onClick, onClose],
  );

  /**
   * В мобилке при открытии списка месяцев или лет нам нужно выключать свайпер,
   * чтобы дать возможность пользователю свайпать скролл у этого контейнера
   **/
  useEffect(() => {
    if (open) {
      swipeController?.disable();
    } else {
      swipeController?.enable();
    }
  }, [open, swipeController]);

  return (
    <>
      {/** Нужно не button https://centrateam.atlassian.net/browse/CA-3733 */}
      <label ref={setRef} data-id={id} onClick={onToggle} className={styles.btn}>
        {children}
        <Icon name="triangle" size="sm" view={isDarkTheme ? 'basicWhite' : 'basicBlack'} />
      </label>

      {ref && (
        <Popover open={open} anchorEl={ref} container={ref}>
          <Box bgColor="basicWhite" paddingTop={8} paddingBottom={8} className={styles.listContainer} column>
            {list.map((value) => (
              <Box
                key={value}
                alignItems="center"
                justifyContent="center"
                paddingTop={8}
                paddingBottom={8}
                paddingLeft={24}
                paddingRight={24}
                onClick={handleClick(value)}
                className={styles.listContainerItem}
              >
                {value}
              </Box>
            ))}
          </Box>
        </Popover>
      )}
    </>
  );
};

interface DatePickerCustomHeaderProps {
  date: Date;
  changeYear: (y: number) => void;
  changeMonth: (m: number) => void;
  theme: TDatePickerTheme;
  externalMonthList?: string[];
  externalYearList?: number[];
  decreaseMonth: (month: number) => void;
  increaseMonth: (month: number) => void;
}

export const DatePickerCustomHeader: FC<DatePickerCustomHeaderProps> = ({
  date,
  changeMonth,
  changeYear,
  theme,
  externalMonthList,
  externalYearList,
  decreaseMonth,
  increaseMonth,
}) => {
  const isDarkTheme = theme === 'dark';

  const handleChange = (kind: TBtnKind, v: number) => {
    const cb = kind === 'month' ? changeMonth : changeYear;

    cb?.(v);
  };

  return (
    <Box alignItems="center" justifyContent="spaceBetween" gap={4} fullWidth>
      <Icon
        id="prevMonthArrow"
        name="outlineLeft"
        size="sm"
        view={isDarkTheme ? 'basicWhite' : 'basicBlack'}
        onClick={() => decreaseMonth(getMonth(date) - 1)}
        className={styles.arrowIcon}
      />
      <Box alignItems="center" justifyContent="center" gap={4}>
        <DatePickerCustomHeaderBtn
          kind="month"
          onClick={handleChange}
          id="DatePickerMonthBtn"
          list={externalMonthList || MONTHS}
          theme={theme}
        >
          <Text id="DatePickerMonth" size="secondary" weight="bold" color={isDarkTheme ? 'basicWhite' : 'basicBlack'}>
            {MONTHS[getMonth(date)]}
          </Text>
        </DatePickerCustomHeaderBtn>

        <DatePickerCustomHeaderBtn
          kind="year"
          onClick={handleChange}
          id="DatePickerYearBtn"
          list={externalYearList || getYearList()}
          theme={theme}
        >
          <Text id="DatePickerYear" size="secondary" weight="bold" color={isDarkTheme ? 'basicWhite' : 'basicBlack'}>
            {getYear(date)}
          </Text>
        </DatePickerCustomHeaderBtn>
      </Box>
      <Icon
        id="nextMonthArrow"
        name="outlineRight"
        size="sm"
        view={isDarkTheme ? 'basicWhite' : 'basicBlack'}
        onClick={() => increaseMonth(getMonth(date) + 1)}
        className={styles.arrowIcon}
      />
    </Box>
  );
};
